/**
 * Stock Conversions — cut / repack / split one PLU's stock into another.
 * E.g. 1 whole cake → 8 slices. Source cost is pooled and split equally
 * across all destination units, then weighted-avg blended into each
 * destination's average_cost_price_excl_vat. Recipe optionally saved on
 * the source product for one-click repeat conversions.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Trash2, Scissors, History } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (n) => `R ${Number(n || 0).toFixed(2)}`;

export default function StockConversions() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [sourceId, setSourceId] = useState("");
  const [sourceQty, setSourceQty] = useState("1");
  const [destinations, setDestinations] = useState([{ product_id: "", qty: "" }]);
  const [note, setNote] = useState("");
  const [saveAsRecipe, setSaveAsRecipe] = useState(false);
  const [sourceQuery, setSourceQuery] = useState("");
  const [destQueries, setDestQueries] = useState([""]);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, h] = await Promise.all([
          axios.get(`${API}/stock/products-for-conversion`),
          axios.get(`${API}/stock/conversions?limit=30`),
        ]);
        // Dedicated lightweight picker endpoint — immune to the
        // Pydantic Product-model validation failures that intermittently
        // 500'd on `/api/products?limit=5000` in production when a
        // legacy row had a null in a required field.
        setProducts(p.data.products || []);
        setHistory(h.data.conversions || []);
      } catch (e) {
        toast.error("Failed to load — " + (e.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // If a recipe exists on the picked source, pre-fill destinations.
  useEffect(() => {
    if (!sourceId) return;
    const s = products.find((p) => p.id === sourceId);
    if (!s?.conversion_yields?.length) return;
    const qty = parseFloat(sourceQty) || 1;
    setDestinations(
      s.conversion_yields.map((y) => ({
        product_id: y.destination_product_id,
        qty: String((y.yield_qty || 0) * qty),
      })),
    );
    setDestQueries(s.conversion_yields.map(() => ""));
  }, [sourceId, sourceQty, products]);

  const sourceProduct = useMemo(() => products.find((p) => p.id === sourceId), [products, sourceId]);
  const sourceCost = Number(sourceProduct?.average_cost_price_excl_vat || 0);
  const valuePool = sourceCost * (parseFloat(sourceQty) || 0);
  const totalDestUnits = destinations.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
  const costPerDest = totalDestUnits > 0 ? valuePool / totalDestUnits : 0;

  const filterProducts = (q) => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return products
      .filter((p) => !p.is_archived)
      .filter(
        (p) =>
          (p.name || "").toLowerCase().includes(query) ||
          (p.plu_code || "").toLowerCase().includes(query) ||
          (p.barcode || "").toLowerCase().includes(query),
      )
      .slice(0, 8);
  };

  const setDestQuery = (i, val) => {
    const copy = [...destQueries];
    copy[i] = val;
    setDestQueries(copy);
  };

  const updateDest = (i, patch) => {
    const copy = [...destinations];
    copy[i] = { ...copy[i], ...patch };
    setDestinations(copy);
  };

  const addDest = () => {
    setDestinations([...destinations, { product_id: "", qty: "" }]);
    setDestQueries([...destQueries, ""]);
  };
  const removeDest = (i) => {
    setDestinations(destinations.filter((_, idx) => idx !== i));
    setDestQueries(destQueries.filter((_, idx) => idx !== i));
  };

  const canSubmit =
    sourceId && parseFloat(sourceQty) > 0 &&
    destinations.length > 0 &&
    destinations.every((d) => d.product_id && parseFloat(d.qty) > 0);

  const handleConvert = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/stock/convert`, {
        source_product_id: sourceId,
        source_qty: parseFloat(sourceQty),
        destinations: destinations.map((d) => ({
          product_id: d.product_id,
          qty: parseFloat(d.qty),
        })),
        note: note.trim() || null,
        save_as_recipe: saveAsRecipe,
      });
      toast.success("Conversion posted");
      setHistory([res.data.conversion, ...history]);
      // Reload products so the freshly-updated stock levels reflect.
      const p = await axios.get(`${API}/stock/products-for-conversion`);
      setProducts(p.data.products || []);
      // Reset input form.
      setSourceQty("1");
      setNote("");
      setSaveAsRecipe(false);
      setDestinations([{ product_id: "", qty: "" }]);
      setDestQueries([""]);
      setSourceQuery("");
      setSourceId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="stock-conversions-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Scissors className="h-6 w-6 text-purple-600" />
          Stock Conversions
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Convert one product into another — e.g. 1 whole cake → 8 slices. Cost is split proportionally.
        </p>
      </div>

      {/* Convert form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">New Conversion</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Source picker */}
          <div>
            <Label>Source Product (what you're breaking down)</Label>
            {sourceProduct ? (
              <div className="mt-1 flex items-center justify-between rounded-md border bg-purple-50 p-2 text-sm">
                <div>
                  <div className="font-semibold">{sourceProduct.name}</div>
                  <div className="text-xs text-gray-600">
                    PLU {sourceProduct.plu_code} · Stock {sourceProduct.current_stock ?? 0} · Avg cost {money(sourceProduct.average_cost_price_excl_vat)}
                    {sourceProduct.conversion_yields?.length ? ` · recipe saved (${sourceProduct.conversion_yields.length} dest)` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSourceId(""); setSourceQuery(""); }} data-testid="clear-source-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Search by name / PLU / barcode"
                  value={sourceQuery}
                  onChange={(e) => setSourceQuery(e.target.value)}
                  data-testid="source-search"
                />
                {sourceQuery && (
                  <div className="mt-1 max-h-56 overflow-y-auto rounded-md border bg-white shadow-sm">
                    {filterProducts(sourceQuery).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setSourceId(p.id); setSourceQuery(""); }}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-purple-50 border-b last:border-0"
                        data-testid={`source-option-${p.plu_code}`}
                      >
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-gray-500">PLU {p.plu_code} · Stock {p.current_stock ?? 0}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Source qty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Source Quantity to Consume</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={sourceQty}
                onChange={(e) => setSourceQty(e.target.value)}
                data-testid="source-qty"
              />
              {sourceProduct && parseFloat(sourceQty) > (sourceProduct.current_stock ?? 0) && (
                <p className="text-xs text-red-600 mt-1">Only {sourceProduct.current_stock ?? 0} in stock</p>
              )}
            </div>
            <div className="text-sm text-gray-700 flex flex-col justify-end pb-1">
              <div>Value pool: <b>{money(valuePool)}</b></div>
              <div>Cost per destination unit: <b>{money(costPerDest)}</b></div>
            </div>
          </div>

          {/* Destinations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">Destinations (what you get)</Label>
              <Button variant="outline" size="sm" onClick={addDest} data-testid="add-dest-btn">
                <Plus className="h-4 w-4 mr-1" /> Add destination
              </Button>
            </div>
            <div className="space-y-2">
              {destinations.map((d, i) => {
                const destProd = products.find((p) => p.id === d.product_id);
                return (
                  <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-start">
                    <div>
                      {destProd ? (
                        <div className="flex items-center justify-between rounded-md border bg-blue-50 px-2 py-1.5 text-sm">
                          <div>
                            <div className="font-medium">{destProd.name}</div>
                            <div className="text-[11px] text-gray-600">PLU {destProd.plu_code} · Stock {destProd.current_stock ?? 0} · Avg {money(destProd.average_cost_price_excl_vat)}</div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => updateDest(i, { product_id: "" })}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Input
                            placeholder="Search destination by name / PLU"
                            value={destQueries[i] || ""}
                            onChange={(e) => setDestQuery(i, e.target.value)}
                            data-testid={`dest-search-${i}`}
                          />
                          {destQueries[i] && (
                            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border bg-white shadow-sm">
                              {filterProducts(destQueries[i]).map((p) => (
                                <button
                                  key={p.id}
                                  onClick={() => { updateDest(i, { product_id: p.id }); setDestQuery(i, ""); }}
                                  className="block w-full px-3 py-1.5 text-left text-xs hover:bg-blue-50 border-b last:border-0"
                                  data-testid={`dest-option-${i}-${p.plu_code}`}
                                >
                                  <div className="font-medium">{p.name}</div>
                                  <div className="text-[11px] text-gray-500">PLU {p.plu_code} · Stock {p.current_stock ?? 0}</div>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Input
                      type="number"
                      placeholder="Qty"
                      min="0.01"
                      step="0.01"
                      value={d.qty}
                      onChange={(e) => updateDest(i, { qty: e.target.value })}
                      data-testid={`dest-qty-${i}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeDest(i)} disabled={destinations.length === 1}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Note (optional)</Label>
            <Input
              placeholder="e.g. Cut whole cake for weekend"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="conversion-note"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={saveAsRecipe}
              onCheckedChange={(v) => setSaveAsRecipe(!!v)}
              data-testid="save-recipe-checkbox"
            />
            Save this pairing as a recipe on the source (one-click next time)
          </label>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleConvert}
              disabled={!canSubmit || submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-conversion-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scissors className="h-4 w-4 mr-2" />}
              Convert
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" /> Recent Conversions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No conversions yet.</p>
          ) : (
            <div className="space-y-2" data-testid="conversion-history">
              {history.map((h) => (
                <div key={h.id} className="rounded border p-3 text-sm bg-white">
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-semibold">
                      {h.source_qty}× {h.source_product_name} <span className="text-gray-400">(PLU {h.source_plu})</span>
                    </div>
                    <div className="text-xs text-gray-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="text-xs text-gray-700">
                    → {(h.destinations || []).map((d) => `${d.qty_received}× ${d.product_name}`).join(", ")}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    Value pool {money(h.value_pool_excl)} · Cost/dest-unit {money(h.cost_per_dest_unit_excl)}
                    {h.note ? ` · Note: ${h.note}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
